
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, X, ExternalLink, Tag, Lock } from "lucide-react";
import { Transaction } from "@/types/payments";
import { Link as RouterLink } from "react-router-dom";
import { useTranslations } from "use-intl";
import { useFormatters } from "@financialharmony/ui";
import TransactionDetailDialog from "./TransactionDetailDialog";
import { TransactionAccountLabel } from "./TransactionAccountLabel";
import { isZeroAmountTransaction } from "@/utils/zeroAmountTransaction";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { TransactionService } from "@/services/transactionService";

const transactionService = new TransactionService();

// FIN-1567 PR5 stub message -- the real booking (FIN-1575) doesn't exist yet, but the request is
// logged server-side (PRIVATE_MARK_REQUESTED) so demand has real evidence.
const NOT_AVAILABLE_YET_MESSAGE = "Marking private isn't available yet. We've been notified.";

interface TransactionsTableProps {
  transactions: Transaction[];
  formatCurrency: (amount: number, currency: string) => string;
  handleManualMatch: (transactionId: string) => void;
  handleMarkAs: (transactionId: string) => void;
  sortKey: keyof Transaction | null;
  sortDirection: 'asc' | 'desc';
  handleSort: (key: keyof Transaction) => void;
  renderSortIcon: (key: keyof Transaction) => React.ReactNode;
  highlightTransactionId?: string | null;
  /** FIN-1021/AC2/AC4: keyed by bank_transactions.id — a waiting-settlement label for this
   * transaction's Match Status cell, next to the matched-document link (never inside Booking
   * Status, which already says "Booked"/"Pending"). */
  waitingSettlementLabels?: Record<string, WaitingState>;
}

type WaitingState = 'waiting' | 'booking' | 'mapped_not_retried' | 'entry_failed';

const WAITING_LABEL_CLASS: Record<WaitingState, string> = {
  waiting: 'border-amber-300 text-amber-800 bg-amber-50',
  booking: 'border-muted-foreground/30 text-muted-foreground bg-muted',
  mapped_not_retried: 'border-amber-300 text-amber-800 bg-amber-50',
  entry_failed: 'border-red-300 text-red-800 bg-red-50',
};

function WaitingSettlementLabel({ state }: { state: WaitingState }) {
  const t = useTranslations('consultantTransactions.table.waiting');
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`ml-2 ${WAITING_LABEL_CLASS[state]}`}>
            {t(`${state}.text`)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{t(`${state}.tooltip`)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// The document a matched transaction links to, in the order a match is resolved.
type MatchedDocumentLabel = 'viewPayment' | 'viewReceivable' | 'viewReceipt' | 'viewPayable' | 'viewInReview';

function matchedDocumentLink(transaction: Transaction): { to: string; label: MatchedDocumentLabel } | null {
  if (transaction.paymentId) return { to: `/account-payables/payments?id=${transaction.paymentId}`, label: 'viewPayment' };
  if (transaction.receivableId) return { to: `/account-receivables?id=${transaction.receivableId}`, label: 'viewReceivable' };
  if (transaction.receiptId) return { to: `/receipts?id=${transaction.receiptId}`, label: 'viewReceipt' };
  if (transaction.payableId) return { to: `/account-payables?id=${transaction.payableId}`, label: 'viewPayable' };
  // FIN-976: an own-account transfer has no document; it links to its verification (learnings/031).
  if (transaction.ownAccountTransfer?.suggestionId) {
    return { to: `/review?suggestion=${encodeURIComponent(transaction.ownAccountTransfer.suggestionId)}`, label: 'viewInReview' };
  }
  return null;
}

const TransactionsTable = ({
  transactions,
  formatCurrency,
  handleManualMatch,
  handleMarkAs,
  sortKey,
  sortDirection,
  handleSort,
  renderSortIcon,
  highlightTransactionId,
  waitingSettlementLabels,
}: TransactionsTableProps) => {
  const t = useTranslations('consultantTransactions.table');
  const { formatDate } = useFormatters();
  // issue #459 — detail dialog state, owned locally so both call sites (Transactions.tsx and
  // TransactionsSection.tsx) get the detail view for free without any prop plumbing. Only the id
  // is stored (not the transaction object itself) and re-derived from the current `transactions`
  // prop on every render, so a refetch while the dialog is open (e.g. after a manual match)
  // reflects the latest data instead of showing a stale snapshot from click time.
  const [detailTransactionId, setDetailTransactionId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const detailTransaction = transactions.find((t) => t.id === detailTransactionId) ?? null;

  const openDetail = (transaction: Transaction) => {
    setDetailTransactionId(transaction.id);
    setDetailOpen(true);
  };

  // FIN-1567 PR5: bulk "Mark N as private" -- selection is offered only on personal-account
  // lines, keyed by bankTransactionId (the real internal row id the backend validates against).
  const [selectedPrivateIds, setSelectedPrivateIds] = useState<Set<string>>(new Set());
  const [markingPrivate, setMarkingPrivate] = useState(false);

  const togglePrivateSelection = (bankTransactionId: string, checked: boolean) => {
    setSelectedPrivateIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(bankTransactionId);
      else next.delete(bankTransactionId);
      return next;
    });
  };

  const handleBulkMarkPrivate = async () => {
    if (selectedPrivateIds.size === 0 || markingPrivate) return;
    setMarkingPrivate(true);
    try {
      await transactionService.markTransactionsPrivate(Array.from(selectedPrivateIds), "mark");
      toast(NOT_AVAILABLE_YET_MESSAGE);
      setSelectedPrivateIds(new Set());
    } catch {
      // handleError (in TransactionApiClient) already surfaced this failure; nothing further to do.
    } finally {
      setMarkingPrivate(false);
    }
  };

  const renderMatchStatus = (transaction: Transaction) => {
    // Reserved (unbooked) transactions are display-only — they are never matched and offer no
    // match actions (issue #363). Legacy rows with no booking_status are treated as booked.
    if (transaction.booking_status === 'reserved') {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          {t('unbooked')}
        </Badge>
      );
    }

    // FIN-804: a 0,00 transaction moved no money, so it is never matchable. The label states only
    // that fact — the bank data carries nothing that says what the row is.
    if (!transaction.matched && isZeroAmountTransaction(transaction)) {
      return (
        <Badge
          variant="outline"
          className="text-muted-foreground"
          title={t('nothingToMatchHint')}
        >
          {t('nothingToMatch')}
        </Badge>
      );
    }

    if (transaction.matched) {
      // issue #523 Task 4 — matchedDocumentReference is rendered as a sibling next to the link,
      // never inside it: putting it inside the <a> would change the link's accessible name and
      // break the existing "View X" role-based lookups above/below this block.
      const target = matchedDocumentLink(transaction);
      const matchLink = target ? (
        <RouterLink to={target.to} className="text-blue-500 hover:underline flex items-center">
          {t(target.label)}
          <ExternalLink className="ml-1 h-3 w-3" />
        </RouterLink>
      ) : null;

      return (
        <div className="flex items-center">
          <Check className="h-4 w-4 text-green-500 mr-2" />
          {matchLink}
          {matchLink && transaction.matchedDocumentReference && (
            <span className="ml-2 text-sm text-muted-foreground">
              {transaction.matchedDocumentReference}
            </span>
          )}
          {/* A match made before 0,00 matches were refused: kept for a human to review, never undone silently. */}
          {isZeroAmountTransaction(transaction) && (
            <Badge variant="destructive" className="ml-2">{t('zeroMatchReview')}</Badge>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <X className="h-4 w-4 text-red-500" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleManualMatch(transaction.id)}
        >
          {t('manuallyMatch')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleMarkAs(transaction.id)}
        >
          <Tag className="h-4 w-4 mr-1" />
          {t('markAs')}
        </Button>
      </div>
    );
  };

  // issue #523 Task 5 — the literal booking status text, always visible (not just the "Unbooked"
  // badge in the match-status column, which only covers the reserved subset). Legacy rows with no
  // booking_status are treated as booked, mirroring renderMatchStatus's existing rule.
  const renderBookingStatusText = (transaction: Transaction) =>
    transaction.booking_status === 'reserved' ? t('pending') : t('booked');

  const hasPersonalAccountRows = transactions.some((t) => t.account?.accountType === "personal");

  return (
    <div className="rounded-md border overflow-x-auto">
      {selectedPrivateIds.size > 0 && (
        <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
          <span className="text-sm font-medium">{t('selected', { count: selectedPrivateIds.size })}</span>
          <Button size="sm" disabled={markingPrivate} onClick={handleBulkMarkPrivate}>
            <Lock className="h-4 w-4 mr-1" />
            {t('markPrivate', { count: selectedPrivateIds.size })}
          </Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {hasPersonalAccountRows && <TableHead className="w-10" />}
            <TableHead className="cursor-pointer" onClick={() => handleSort('date')}>
              {t('columns.date')}
              {renderSortIcon('date')}
            </TableHead>
            <TableHead>{t('columns.account')}</TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>
              {t('columns.description')}
              {renderSortIcon('description')}
            </TableHead>
            <TableHead>{t('columns.counterparty')}</TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('amount')}>
              {t('columns.amount')}
              {renderSortIcon('amount')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('currency')}>
              {t('columns.currency')}
              {renderSortIcon('currency')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('type')}>
              {t('columns.type')}
              {renderSortIcon('type')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('category')}>
              {t('columns.category')}
              {renderSortIcon('category')}
            </TableHead>
            <TableHead>{t('columns.bookingStatus')}</TableHead>
            <TableHead>{t('columns.matchStatus')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.length > 0 ? (
            transactions.map((transaction) => {
              const isHighlighted = highlightTransactionId === transaction.id;
              const nothingToMatch = !transaction.matched && isZeroAmountTransaction(transaction);
              const isPersonalAccount = transaction.account?.accountType === "personal";
              return (
                <TableRow
                  key={transaction.id}
                  className={`cursor-pointer hover:bg-muted/60 transition-colors ${isHighlighted ? "bg-blue-50 border-blue-200" : ""} ${nothingToMatch ? "text-muted-foreground" : ""}`}
                  onClick={() => openDetail(transaction)}
                >
                  {hasPersonalAccountRows && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {isPersonalAccount && transaction.bankTransactionId && (
                        <Checkbox
                          aria-label={t('selectForPrivate', { description: transaction.description })}
                          checked={selectedPrivateIds.has(transaction.bankTransactionId)}
                          onCheckedChange={(checked) =>
                            togglePrivateSelection(transaction.bankTransactionId!, checked === true)
                          }
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell className={isHighlighted ? "font-semibold" : ""}>
                    {formatDate(transaction.date, 'numeric')}
                  </TableCell>
                  <TableCell className="max-w-[16rem]">
                    <TransactionAccountLabel transaction={transaction} surface="table" />
                  </TableCell>
                  <TableCell className={isHighlighted ? "font-semibold" : ""}>
                    {transaction.description}
                  </TableCell>
                  <TableCell>
                    {transaction.counterpartyName}
                    {transaction.counterpartyIban && (
                      <div className="text-xs text-muted-foreground">
                        {transaction.counterpartyIban}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{formatCurrency(transaction.amount, transaction.currency)}</TableCell>
                  <TableCell>{transaction.currency}</TableCell>
                  <TableCell>
                    {isZeroAmountTransaction(transaction) ? (
                      <span className="text-muted-foreground">–</span>
                    ) : (
                      <Badge variant={transaction.type === 'credit' ? 'default' : 'outline'}>
                        {transaction.type === 'credit' ? t('credit') : t('debit')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {nothingToMatch ? t('notApplicable') : transaction.category || t('uncategorized')}
                  </TableCell>
                  <TableCell>{renderBookingStatusText(transaction)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center">
                      {renderMatchStatus(transaction)}
                      {transaction.bankTransactionId && waitingSettlementLabels?.[transaction.bankTransactionId] && (
                        <WaitingSettlementLabel state={waitingSettlementLabels[transaction.bankTransactionId]} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={hasPersonalAccountRows ? 11 : 10} className="h-24 text-center">
                {t('empty')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TransactionDetailDialog
        transaction={detailTransaction}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default TransactionsTable;
